<template>
  <div>
    <div class="brand-card-lg">
      <div v-if="loading">
        <HeaderLoader />
      </div>
      <div v-else class="sm:flex sm:items-center sm:justify-between space-y-3 pb-5 sm:space-y-0">
        <div class="space-x-6 flex items-center">
          <p class="inline title">Recovered Revenue</p>
          <p class="text-sm space-x-2 font-semibold text-gray-700 flex items-center">
            <font-awesome-icon :icon="['fas', 'square']" class="text-xl text-brand-blue"></font-awesome-icon>
            <span>Recovered</span>
          </p>
          <p class="text-sm space-x-2 font-semibold text-gray-700 flex items-center">
            <font-awesome-icon :icon="['fas', 'square']" class="text-xl text-brand-gray-blue"></font-awesome-icon>
            <span>Actively Recovering</span>
          </p>
        </div>
      </div>
      <div>
        <div v-if="loading">
          <BarLoader />
          <FooterLoader />
        </div>
        <div v-else-if="chartVegaSpec">
          <vue-vega-embed width="auto" :height="200" :spec="chartVegaSpec"></vue-vega-embed>
          <div v-if="statLines.length" class="ml-12">
            <div class="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:pt-4 text-center lg:text-left">
              <div v-for="stat in statLines" :key="stat.label" class="flex flex-col space-y-2">
                <p class="text-base text-gray-600 font-semibold">{{ stat.label }}</p>
                <p class="text-2xl xl:text-3xl text-brand-blue font-bold">{{ stat.primary }}</p>
                <p class="font-semibold" :class="[stat.subColor]">{{ stat.sub }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import StatsService from '@/api/stats.service';
import VueVegaEmbed from '@/components/Vis/Vega/VueVegaEmbed.vue';
import { genBarSpec } from '@/components/Vis/Vega/Specs/BarSpec';
import { timeDay, timeMinute, timeMonth } from 'd3-time';
import { dunningCampaignOutcomeMap, LOAD_STATE } from '@/store/shared';
import { mapGetters } from 'vuex';
import { min } from 'd3-array';
import { shortMonthYearLabel } from '@/helpers/date-utils';
import { DUNNING_CAMPAIGN, currencyFormat } from '@/helpers/stats-utils';
import BarLoader from '@/components/UI/Loader/BarLoader.vue';
import FooterLoader from '@/components/UI/Loader/FooterLoader.vue';
import HeaderLoader from '@/components/UI/Loader/HeaderLoader.vue';
import { formatSmallMoney, formatSmallNumber } from '@/helpers/jsUtils';

export default Vue.extend({
  name: 'DunningRecovered',
  components: {
    VueVegaEmbed,
    BarLoader,
    HeaderLoader,
    FooterLoader,
  },
  data() {
    return {
      LOAD_STATE,
      statsByOutcomeMonth: [],
      statsByMonth: [],
      loadState: LOAD_STATE.UNLOADED,
      summaryStats: {},
    };
  },
  async created() {
    this.loadState = LOAD_STATE.LOADING;
    try {
      const outcomeMonthBreakdown = await StatsService.dunningCampaignCountUsd({
        breakdown: [DUNNING_CAMPAIGN.INVOICE_MONTH, DUNNING_CAMPAIGN.OUTCOME],
      });
      outcomeMonthBreakdown.sort((a, b) => new Date(a.invoiceMonth).getTime() - new Date(b.invoiceMonth).getTime());
      this.statsByOutcomeMonth = outcomeMonthBreakdown;
      const fieldsToAggregate = [
        'recoveredAmountUsd',
        'pendingAmountUsd',
        'count',
        'pendingCount',
        'invoiceAmountUsd',
        'lostCount',
        'lostAmountUsd',
        'recoveredAmount',
        'pendingAmount',
        'lostAmount',
      ];
      const baseAgg = fieldsToAggregate.reduce((acc, curr) => {
        acc[curr] = 0;
        return acc;
      }, {});
      // build the stats (broken down by month) from the outcomeMonthBreakdown
      const statsByMonth = outcomeMonthBreakdown.reduce((acc, curr) => {
        const month = curr.invoiceMonth;
        if (!acc[month]) {
          acc[month] = { ...baseAgg, currencyBreakdown: {}, invoiceMonth: month };
        }
        fieldsToAggregate.forEach((field) => {
          acc[month][field] += curr[field];
        });
        Object.keys(curr.currencyBreakdown).forEach((currency) => {
          if (!acc[month].currencyBreakdown[currency]) {
            acc[month].currencyBreakdown[currency] = { ...curr.currencyBreakdown[currency] };
          } else {
            fieldsToAggregate.forEach((field) => {
              acc[month].currencyBreakdown[currency][field] += curr.currencyBreakdown[currency][field];
            });
          }
        });
        return acc;
      }, {});
      const sortedStatsByMonth = Object.keys(statsByMonth)
        .sort()
        .map((key) => statsByMonth[key]);
      this.statsByMonth = sortedStatsByMonth;
      const summaryStats = sortedStatsByMonth.reduce(
        (acc, curr) => {
          fieldsToAggregate.forEach((field) => {
            acc[field] += curr[field];
          });
          return acc;
        },
        { ...baseAgg }
      );
      this.summaryStats = summaryStats;
      this.loadState = LOAD_STATE.LOADED;
    } catch (e) {
      console.log(e);
      this.loadState = LOAD_STATE.ERROR;
    }
  },
  computed: {
    ...mapGetters([]),
    loading() {
      return this.loadState !== LOAD_STATE.LOADED;
    },
    recoveredBySource() {
      return this.statsByOutcomeMonth.reduce((acc, curr) => {
        if (!acc[curr.outcome]) {
          acc[curr.outcome] = curr.recoveredAmountUsd;
        } else {
          acc[curr.outcome] += curr.recoveredAmountUsd;
        }
        return acc;
      }, {});
    },
    statLines() {
      const lines = [];
      if (this.loadState !== LOAD_STATE.LOADED || !this.summaryStats.count) {
        return [];
      }
      if (this.summaryStats.count) {
        lines.push({
          label: 'Successfully Recovered',
          primary: formatSmallMoney(this.summaryStats.recoveredAmountUsd),
          sub: `${this.summaryStats.count} ${this.summaryStats.count === 1 ? 'successful campaign' : 'successful campaigns'}`,
          subColor: 'text-gray-500 text-sm',
        });
      }
      if (this.summaryStats.pendingCount) {
        lines.push({
          label: 'Actively Recovering',
          primary: formatSmallMoney(this.summaryStats.pendingAmountUsd),
          sub: `${this.summaryStats.pendingCount} ${this.summaryStats.pendingCount === 1 ? 'active campaign' : 'active campaigns'}`,
          subColor: 'text-gray-500 text-sm',
        });
      }
      return lines;
    },
    chartVegaSpec() {
      if (!this.statsByMonth || !this.statsByMonth.length) return null;

      const now = new Date();
      const orgCreatedDate = new Date(min(this.statsByMonth, (d) => d.invoiceMonth));
      const currencies = [...new Set(this.statsByMonth.flatMap((s) => Object.keys(s.currencyBreakdown)))];
      const uniqueOutcomes = [
        ...new Set(this.statsByOutcomeMonth.flatMap((s) => (dunningCampaignOutcomeMap[s.outcome] ? [dunningCampaignOutcomeMap[s.outcome]] : []))),
      ];

      const chartData = this.statsByMonth.reduce((acc, item) => {
        const outcomeBreakdownMap = this.statsByOutcomeMonth
          .filter((d) => d.invoiceMonth === item.invoiceMonth)
          .reduce((map, obj) => {
            if (dunningCampaignOutcomeMap[obj.outcome]) {
              if (!map[dunningCampaignOutcomeMap[obj.outcome]]) map[dunningCampaignOutcomeMap[obj.outcome]] = 0;
              map[dunningCampaignOutcomeMap[obj.outcome]] += obj.recoveredAmountUsd;
            }
            return map;
          }, {});

        const offsetTime = timeDay.offset(new Date(item.invoiceMonth), 1);
        const invoiceMonth = new Date(offsetTime);
        const monthsSince = timeMonth.count(orgCreatedDate, invoiceMonth);

        const cs = currencies.reduce((cacc, currency) => {
          const v = item.currencyBreakdown[currency]?.recoveredAmount || 0;
          cacc[currency] = currencyFormat(v, currency);
          return cacc;
        }, {});

        const os = uniqueOutcomes.reduce((oacc, outcome) => {
          const v = outcomeBreakdownMap[outcome] || 0;
          oacc[outcome] = currencyFormat(v, 'usd');
          return oacc;
        }, {});

        const blankOutcomes = uniqueOutcomes.reduce((oacc, outcome) => {
          oacc[outcome] = 'In Progress...';
          return oacc;
        }, {});

        // don't include current month if its within the first 2 days
        if (timeDay.count(invoiceMonth, now) >= 7) {
          if (true || item.recoveredAmountUsd) {
            acc.push({
              'Campaign Revenue': item.recoveredAmountUsd,
              Month: shortMonthYearLabel(invoiceMonth),
              'Months with Churnkey': monthsSince,
              Outcome: 'Recovered',
              ...(Object.keys(cs).length > 1 ? { ...cs } : {}),
              ...(Object.keys(os).length > 0 ? { ...os } : {}),
            });
          }
          if (true || item.pendingAmountUsd) {
            const cp = currencies.reduce((cacc, currency) => {
              const v = item.currencyBreakdown[currency]?.pendingAmount || 0;
              cacc[currency] = currencyFormat(v, currency);
              return cacc;
            }, {});
            acc.push({
              'Campaign Revenue': item.pendingAmountUsd,
              Month: shortMonthYearLabel(invoiceMonth),
              'Months with Churnkey': monthsSince,
              Outcome: 'Active Campaign',
              ...(Object.keys(cp).length > 1 ? { ...cp } : {}),
              ...(Object.keys(blankOutcomes).length > 0 ? { ...blankOutcomes } : {}),
            });
          }
        }
        return acc;
      }, []);

      chartData.sort((a, b) => a['Months with Churnkey'] - b['Months with Churnkey']);

      return genBarSpec({
        data: chartData,
        xField: 'Month',
        xSortField: 'Months with Churnkey',
        yField: 'Campaign Revenue',
        colorField: 'Outcome',
        xLabels: true,
        yLabels: true,
        barLabels: true,
        yFormat: '$0.2s',
        colorScale: {
          domain: ['Recovered', 'Active Campaign'],
          range: ['#3c5ccf', '#BAC5EE'],
        },
        xAxisTitle: 'Invoice Month',
        tooltipFields: [
          { field: 'Month', title: 'Month' },
          { field: 'Campaign Revenue', title: 'Campaign Revenue', format: '$0.2s', type: 'quantitative' },
          ...(currencies.length > 1 ? currencies.map((c) => ({ field: c, title: c })) : []),
          ...uniqueOutcomes.map((o) => ({ field: o, title: o })),
        ],
      });
    },
  },
});
</script>
